import { useEffect, useEffectEvent } from 'react';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { useShoppingContext } from 'contexts/ShoppingContext';
import { useSlotContext } from 'contexts/Slot';
import { useCustomerOrderContext } from 'contexts/CustomerOrder';
import membershipServiceClient, { MY_WAITROSE_MEMBERSHIP } from 'services/membership';

const deriveMyWaitroseStatus = (
  membershipNumber: string | undefined,
  error: boolean,
): 'Y' | 'N' | undefined => {
  if (membershipNumber) return 'Y';
  if (error) return undefined;
  return 'N';
};

export const UserTracking = (): null => {
  const { state } = useAuthentication();
  const { trackEvent } = useAnalytics();
  const { shoppingContext } = useShoppingContext();
  const { slotContext } = useSlotContext();
  const { customerOrderStatsContext } = useCustomerOrderContext();

  const trackUser = useEffectEvent((user: Record<string, unknown>) =>
    trackEvent({ event: 'user', user }),
  );

  useEffect(() => {
    if (!state.initialised) return;

    trackUser({
      customer_id: state.isLoggedIn ? (state.customerId ?? '-1') : '-1',
      login_status: state.isLoggedIn,
    });
  }, [state.initialised, state.isLoggedIn, state.customerId]);

  useEffect(() => {
    if (!state.isLoggedIn || !customerOrderStatsContext) return;

    trackUser({
      customer_type: customerOrderStatsContext?.customerHasACompletedOrder ? 'Existing' : 'New',
      days_since_last_shop: customerOrderStatsContext?.daysSinceLastCompletedOrder,
    });
  }, [state.isLoggedIn, customerOrderStatsContext]);

  useEffect(() => {
    if (!state.isLoggedIn || !state.token?.authHeader) return;

    const getMyWaitroseNumber = async () => {
      const { membership, error } = await membershipServiceClient.getMemberships(
        state.token?.authHeader!,
      );

      const myWaitroseNumber = Array.isArray(membership)
        ? membership.find(member => member.type === MY_WAITROSE_MEMBERSHIP)?.number
        : undefined;

      trackUser({
        mywaitrose_customer: deriveMyWaitroseStatus(myWaitroseNumber, !!error),
      });
    };

    getMyWaitroseNumber();
  }, [state.isLoggedIn, state.token?.authHeader]);

  useEffect(() => {
    if (!slotContext.details?.branchId) return;

    trackUser({
      branch_id: slotContext.details.branchId,
    });
  }, [
    state.isLoggedIn,
    shoppingContext.customerOrderId,
    slotContext.details?.branchId,
    slotContext.loading,
  ]);

  return null;
};
